import React, { useEffect, useState } from 'react';
import { fetchHormoneData } from '../../lib/userDataService';

/**
 * HormoneTrendsScreen — Cycle-specific hormone experience
 *
 * Educational & predictive, NOT a laboratory viewer. The Hormones tab
 * (in the main bottom nav) already handles report uploads + lab history.
 * Here we explain expected hormone behaviour for the user's *current*
 * phase, and – if reports exist – overlay how the user's most recent
 * lab values compare against phase-typical ranges.
 *
 * Layout borrows the same editorial rhythm as Today's Intelligence
 * (cx-intel-eyebrow / cx-intel-h1 / cx-intel-section) so it feels
 * native to the Cycle flow.
 */

const HORMONE_PROFILES = {
  menstrual: {
    headline: 'A quiet reset.',
    lead: 'Estrogen and progesterone are at their lowest. Your body starts building the next cycle.',
    rows: [
      { key: 'estrogen',     label: 'Estrogen',     level: 'Low',      dir: 'flat',  tone: 'quiet' },
      { key: 'progesterone', label: 'Progesterone', level: 'Very low', dir: 'flat',  tone: 'quiet' },
      { key: 'lh',           label: 'LH',           level: 'Baseline', dir: 'flat',  tone: 'quiet' },
      { key: 'fsh',          label: 'FSH',          level: 'Rising',   dir: 'up',    tone: 'brand' },
    ],
  },
  follicular: {
    headline: 'Estrogen begins to climb.',
    lead: 'FSH is recruiting a follicle. Estrogen rises steadily — expect energy and mood to lift.',
    rows: [
      { key: 'estrogen',     label: 'Estrogen',     level: 'Climbing', dir: 'up',    tone: 'brand' },
      { key: 'progesterone', label: 'Progesterone', level: 'Low',      dir: 'flat',  tone: 'quiet' },
      { key: 'lh',           label: 'LH',           level: 'Building', dir: 'up',    tone: 'brand' },
      { key: 'fsh',          label: 'FSH',          level: 'Elevated', dir: 'up',    tone: 'brand' },
    ],
  },
  ovulatory: {
    headline: 'The LH surge.',
    lead: 'Estrogen peaks and triggers an LH spike — your body\u2019s highest fertility window.',
    rows: [
      { key: 'estrogen',     label: 'Estrogen',     level: 'Peak',     dir: 'up',    tone: 'brand' },
      { key: 'progesterone', label: 'Progesterone', level: 'Rising',   dir: 'up',    tone: 'brand' },
      { key: 'lh',           label: 'LH',           level: 'Surge',    dir: 'up',    tone: 'brand' },
      { key: 'fsh',          label: 'FSH',          level: 'Falling',  dir: 'down',  tone: 'quiet' },
    ],
  },
  luteal: {
    headline: 'Progesterone takes over.',
    lead: 'The corpus luteum produces progesterone to prepare the uterine lining. Estrogen dips, then rises again mid-luteal before both drop.',
    rows: [
      { key: 'estrogen',     label: 'Estrogen',     level: 'Falling',  dir: 'down',  tone: 'quiet' },
      { key: 'progesterone', label: 'Progesterone', level: 'Peak',     dir: 'up',    tone: 'brand' },
      { key: 'lh',           label: 'LH',           level: 'Quiet',    dir: 'flat',  tone: 'quiet' },
      { key: 'fsh',          label: 'FSH',          level: 'Quiet',    dir: 'flat',  tone: 'quiet' },
    ],
  },
  waiting: {
    headline: 'Your cycle is running long.',
    lead: 'When ovulation is delayed, hormones can plateau. Your report — if uploaded — helps clarify the pattern.',
    rows: [
      { key: 'estrogen',     label: 'Estrogen',     level: 'Uncertain', dir: 'flat', tone: 'quiet' },
      { key: 'progesterone', label: 'Progesterone', level: 'Low',       dir: 'flat', tone: 'quiet' },
      { key: 'lh',           label: 'LH',           level: 'Variable',  dir: 'flat', tone: 'quiet' },
      { key: 'fsh',          label: 'FSH',          level: 'Variable',  dir: 'flat', tone: 'quiet' },
    ],
  },
};

const arrow = { up: '↑', down: '↓', flat: '·' };

export default function HormoneTrendsScreen({ userId, phase = 'luteal' }) {
  const [labs, setLabs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const h = await fetchHormoneData(userId);
      if (alive) { setLabs(h); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [userId]);

  const profile = HORMONE_PROFILES[phase] || HORMONE_PROFILES.luteal;
  const hasLabs = !!labs;

  return (
    <div className="cx-sub-body" data-testid="hormone-trends-screen">
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">{phase.toUpperCase()} · What your hormones are doing</div>
        <h1 className="cx-intel-h1">{profile.headline}</h1>
        <p className="cx-sentence" style={{ marginTop: 14, maxWidth: 360 }}>{profile.lead}</p>
        <div className="cx-intel-conf-chip" style={{ marginTop: 18 }}>
          <span className="cx-conf-dot" />
          {hasLabs ? 'Personalised · lab data overlaid' : 'Educational estimate · no labs yet'}
        </div>
      </section>

      <section className="cx-intel-section">
        <div className="cx-intel-label">Expected pattern this phase</div>
        <div className="cx-hormone-grid" data-testid="phase-hormone-grid">
          {profile.rows.map((r) => (
            <HormoneRow key={r.key} row={r} lab={labs?.[r.key]} />
          ))}
        </div>
      </section>

      {!hasLabs && !loading && (
        <section className="cx-intel-section">
          <div className="cx-intel-label">Get personalised trends</div>
          <div className="cx-glass" style={{ padding: 18, borderRadius: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--cx-body)', lineHeight: 1.55 }}>
              Upload your latest hormone panel from the <strong>Health</strong> tab. I&rsquo;ll compare
              your LH, FSH, estrogen and progesterone against phase-typical ranges and tell
              you what shifts to expect from your body.
            </p>
          </div>
        </section>
      )}

      {hasLabs && (
        <section className="cx-intel-section">
          <div className="cx-intel-label">Your latest values</div>
          <div className="cx-hormone-labs" data-testid="lab-overlay">
            {['lh', 'fsh', 'testosterone', 'amh'].map((k) => (
              labs[k] != null && (
                <div key={k} className="cx-hormone-lab-row">
                  <span className="cx-lab-name">{k.toUpperCase()}</span>
                  <span className="cx-lab-value">{labs[k]}</span>
                </div>
              )
            ))}
          </div>
          <p className="cx-fine-print" style={{ marginTop: 12 }}>
            Educational overlay only. Discuss values with a clinician for interpretation.
          </p>
        </section>
      )}

      <section className="cx-intel-section">
        <div className="cx-intel-label">Why this matters for PCOS</div>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--cx-body)', lineHeight: 1.6 }}>
          In PCOS, the LH-to-FSH ratio is often elevated and ovulation can be delayed or absent.
          Tracking hormones across cycles — not just once — reveals your own baseline rhythm.
        </p>
      </section>
    </div>
  );
}

function HormoneRow({ row, lab }) {
  const isDominant = row.tone === 'brand';
  return (
    <div
      className={`cx-hormone-row ${isDominant ? 'cx-hormone-row--dominant' : ''}`}
      data-h={row.key}
    >
      <span className="cx-hormone-name">{row.label}</span>
      <span className="cx-hormone-level">{row.level}</span>
      <span className={`cx-hormone-arrow cx-hormone-arrow--${row.dir}`}>{arrow[row.dir]}</span>
      {lab != null && <span className="cx-hormone-value">{lab}</span>}
    </div>
  );
}
